import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FirestoreService from '../assets/js/firestore_instance';
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const ProfileSection = ({ organization, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    organizationName: organization?.organizationName || '',
    email: organization?.email || '',
    phone: organization?.phone || '',
    organizationWebsite: organization?.organizationWebsite || '',
    address: {
      street: organization?.address?.street || '',
      city: organization?.address?.city || '',
      state: organization?.address?.state || '',
      zip: organization?.address?.zip || '',
    },
    about: organization?.about || '',
    subscription: organization?.subscription || 'none',
  });
  
  // Update the useEffect hook:
  useEffect(() => {
    if (organization) {
      setFormData({
        organizationName: organization.organizationName || '',
        email: organization.email || '',
        phone: organization.phone || '',
        organizationWebsite: organization.organizationWebsite || '',
        address: {
          street: organization?.address?.street || '',
          city: organization?.address?.city || '',
          state: organization?.address?.state || '',
          zip: organization?.address?.zip || '',
        },
        about: organization?.about || '',
        subscription: organization.subscription || 'none',
      });
    }
  }, [organization]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      // Handle regular fields
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const db = FirestoreService.getFirestore();
      const storage = FirestoreService.getStorage();
      const orgRef = doc(db, "organizations", organization.organizationId);
      
      let updateData = { ...formData };
      
      // Upload logo if changed
      if (logo) {
        const orgNameWithoutSpaces = formData.organizationName.replace(/\s+/g, '-').toLowerCase();
        const logoRef = ref(storage, `organization-logos/${orgNameWithoutSpaces}_${organization.organizationId}`);
        await uploadBytes(logoRef, logo);
        const logoUrl = await getDownloadURL(logoRef);
        updateData.organizationAvatar = logoUrl;
      }
      
      await updateDoc(orgRef, updateData);

      // Fetch the updated document
      const updatedOrgDoc = await getDoc(orgRef);
      if (updatedOrgDoc.exists() && onUpdate) {
        // Call the callback with fresh data
        onUpdate({
          ...updatedOrgDoc.data(),
          organizationId: organization.organizationId
        });
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating organization: ", err);
      setError("Failed to update organization information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEdit = () => {
    if (organization) {
      setFormData({
        organizationName: organization.organizationName || '',
        email: organization.email || '',
        phone: organization.phone || '',
        organizationWebsite: organization.organizationWebsite || '',
        address: {
          street: organization?.address?.street || '',
          city: organization?.address?.city || '',
          state: organization?.address?.state || '',
          zip: organization?.address?.zip || '',
        },
        about: organization?.about || '',
        subscription: organization.subscription || 'none',
        orgType: organization.orgType || 'joltchat',
      });
    }
    setLogo(null);
    setLogoPreview(null);
    setIsEditing(false);
  };

  return (
    <div className="dashboard-element-bg rounded-xl p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Organization Profile</h1>
          {!isEditing && (
            <motion.button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Edit Profile
            </motion.button>
          )}
        </div>

        <AnimatePresence mode="wait">
          {success && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-3 bg-green-600 text-white rounded-lg"
            >
              Profile updated successfully!
            </motion.div>
          )}

          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-3 bg-red-600 text-white rounded-lg"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="dashboard-on-element rounded-lg p-6 shadow-md">
          {isEditing ? (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="col-span-1 md:col-span-2 flex flex-col md:flex-row gap-6 items-start">
                  <div className="w-full md:w-1/3">
                    <label className="block mb-2 text-sm font-medium">Organization Logo</label>
                    <div className="relative group">
                      <div className="w-32 h-32 bg-white rounded-lg overflow-hidden flex items-center justify-center">
                        {logoPreview || organization?.organizationAvatar ? (
                          <img 
                            src={logoPreview || organization?.organizationAvatar} 
                            alt="Organization Logo" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-gray-400 text-center">No Logo</div>
                        )}
                      </div>
                      <label className="block mt-2 cursor-pointer">
                        <span className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm">Change Logo</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleLogoChange}
                        />
                      </label>
                    </div>
                  </div>
                  
                  <div className="w-full md:w-2/3">
                    <div className="mb-4">
                      <label className="block mb-2 text-sm font-medium">Organization Name</label>
                      <input
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleChange}
                        className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block mb-2 text-sm font-medium">About Us</label>
                      <textarea
                        name="about"
                        value={formData.about}
                        onChange={handleChange}
                        className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[80px]"
                        placeholder="Tell us about your organization..."
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                
                <div className="col-span-1 md:col-span-2">
                  <label className="block mb-2 text-sm font-medium">Website</label>
                  <input
                    type="url"
                    name="organizationWebsite"
                    value={formData.organizationWebsite}
                    onChange={handleChange}
                    className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div className="col-span-1 md:col-span-2">
  <label className="block mb-2 text-sm font-medium">Street Address</label>
  <input
    type="text"
    name="address.street"
    value={formData.address.street}
    onChange={handleChange}
    className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
  />
</div>

<div>
  <label className="block mb-2 text-sm font-medium">City</label>
  <input
    type="text"
    name="address.city"
    value={formData.address.city}
    onChange={handleChange}
    className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
  />
</div>

<div className="grid grid-cols-2 gap-3">
  <div>
    <label className="block mb-2 text-sm font-medium">State</label>
    <input
      type="text"
      name="address.state"
      value={formData.address.state}
      onChange={handleChange}
      className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
    />
  </div>
  
  <div>
    <label className="block mb-2 text-sm font-medium">ZIP Code</label>
    <input
      type="text"
      name="address.zip"
      value={formData.address.zip}
      onChange={handleChange}
      className="w-full p-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
    />
  </div>
                </div>
              </div>
              
              <div className="mt-8 flex justify-end gap-3">
                <motion.button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isLoading}
                >
                  Cancel
                </motion.button>
                
                <motion.button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg shadow-md flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : "Save Changes"}
                </motion.button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2 flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-1/3">
                  <h3 className="text-lg font-medium mb-2">Organization Logo</h3>
                  <div className="w-32 h-32 bg-white rounded-lg overflow-hidden flex items-center justify-center">
                    {organization?.organizationAvatar ? (
                      <img 
                        src={organization.organizationAvatar} 
                        alt="Organization Logo" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-gray-400 text-center">No Logo</div>
                    )}
                  </div>
                </div>
                
                <div className="w-full md:w-2/3">
                  <h2 className="text-2xl font-bold mb-2">{organization?.organizationName || 'Organization Name'}</h2>
                  <div className="text-gray-300 mb-2">
                    Subscription: {organization?.subscription === 'founders' ? 'Founders' : 'None'}
                  </div>
                  <h3 className="text-lg font-medium mt-4">About Us</h3>
                    <p className="text-gray-300">
                        {organization?.about || 'No description provided'}
                    </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-2">Contact Information</h3>
                <div className="space-y-2 text-gray-300">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                    </svg>
                    {organization?.email || 'No email provided'}
                  </div>
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                    </svg>
                    {organization?.phone || 'No phone provided'}
                  </div>
                  {organization?.organizationWebsite && (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                      </svg>
                      <a href={organization.organizationWebsite} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
                        {organization.organizationWebsite}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
  <h3 className="text-lg font-medium mb-2">Address</h3>
  <div className="space-y-1 text-gray-300">
    {organization?.address?.street && <div>{organization.address.street}</div>}
    {(organization?.address?.city || organization?.address?.state || organization?.address?.zip) && (
      <div>
        {organization?.address?.city}{organization?.address?.city && (organization?.address?.state || organization?.address?.zip) ? ', ' : ''}
        {organization?.address?.state} {organization?.address?.zip}
      </div>
    )}
    {!organization?.address?.street && !organization?.address?.city && !organization?.address?.state && !organization?.address?.zip && (
      <div>No address provided</div>
    )}
  </div>
</div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
  
export default ProfileSection;